// Cloud-backed Repository. Wraps the local IndexedDB repo so we always have a
// fresh local mirror, and pushes mutations to /api/sync (debounced) on top.
//
// load() runs the merge: it pulls cloud, reads the local mirror, runs
// mergeSnapshots, persists the merged result back to local AND to cloud, and
// returns it. Subsequent saves push merged in-app changes up.

import { migrate } from './migrations';
import { mergeSnapshots, type MergeResult } from './merge';
import type { AppSnapshot, Repository } from './repository';
import { localRepository } from './localRepository';

interface SyncedRepoOptions {
  endpoint: string;
  /** Returns the current bearer token; called every request so refreshes flow through. */
  getToken: () => string | null;
  /** Optional — invoked once after load() if both sides had divergent data. */
  onMergeReport?: (report: MergeResult) => void;
  debounceMs?: number;
}

export class SyncedRepository implements Repository {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pending: AppSnapshot | null = null;
  private debounceMs: number;

  constructor(private opts: SyncedRepoOptions) {
    this.debounceMs = opts.debounceMs ?? 1500;
  }

  async load(): Promise<AppSnapshot> {
    const local = await localRepository.load();
    let serverSnap: AppSnapshot | null = null;

    try {
      const res = await fetch(this.opts.endpoint, {
        method: 'GET',
        headers: this.headers(),
      });
      if (res.ok) {
        const body = (await res.json()) as { snapshot: unknown | null };
        if (body?.snapshot && typeof body.snapshot === 'object') {
          // Run the migration so old-version cloud rows get up-leveled.
          serverSnap = migrate(body.snapshot);
        }
      } else if (res.status === 401) {
        console.warn('[sync] unauthorized — falling back to local only');
        return local;
      } else {
        console.warn('[sync] GET failed', res.status);
      }
    } catch (err) {
      console.warn('[sync] GET network error, using local', err);
    }

    if (!serverSnap) {
      // First sync for this user: push local up so the cloud has a baseline.
      void this.pushImmediately(local);
      return local;
    }

    const report = mergeSnapshots(local, serverSnap);
    this.opts.onMergeReport?.(report);
    await localRepository.save(report.merged);
    // Push the merged snapshot back so the cloud reflects the union.
    void this.pushImmediately(report.merged);
    return report.merged;
  }

  async save(snapshot: AppSnapshot): Promise<void> {
    // Always keep the local mirror fresh.
    await localRepository.save(snapshot);
    this.pending = snapshot;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      void this.flush();
    }, this.debounceMs);
  }

  private async flush(): Promise<void> {
    const snap = this.pending;
    if (!snap) return;
    this.pending = null;
    try {
      const res = await fetch(this.opts.endpoint, {
        method: 'PUT',
        headers: { 'content-type': 'application/json', ...this.headers() },
        body: JSON.stringify({ snapshot: snap }),
      });
      if (!res.ok) {
        console.warn('[sync] PUT failed', res.status);
      }
    } catch (err) {
      console.warn('[sync] PUT network error', err);
    }
  }

  private async pushImmediately(snap: AppSnapshot): Promise<void> {
    try {
      await fetch(this.opts.endpoint, {
        method: 'PUT',
        headers: { 'content-type': 'application/json', ...this.headers() },
        body: JSON.stringify({ snapshot: snap }),
      });
    } catch (err) {
      console.warn('[sync] initial PUT failed', err);
    }
  }

  private headers(): Record<string, string> {
    const token = this.opts.getToken();
    return token ? { authorization: `Bearer ${token}` } : {};
  }
}
